"use client"

import { useEffect, useRef, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import Autoplay from "embla-carousel-autoplay"
import useEmblaCarousel from "embla-carousel-react"
import { ArrowRight } from "lucide-react"

import fallback from "@/public/fallback.png"
import type { IArticle } from "@/types"

const REVIEW_IMAGE_WIDTH = 296
const REVIEW_SLIDE_GAP = 24
const SLIDES_TO_SCROLL = 2

export type ResearchReview = Pick<
  IArticle,
  "id" | "title" | "slug" | "abstract" | "images" | "categories"
> & {
  description?: string
  priority?: number | string
  type?: number | string
}

export default function ResearchReviewsCarousel({
  reviews,
}: {
  reviews: ResearchReview[]
}) {
  const [eagerImageIndexes, setEagerImageIndexes] = useState(
    () =>
      new Set(Array.from({ length: Math.min(5, reviews.length) }, (_, i) => i))
  )
  const autoplay = useRef(
    Autoplay({
      delay: 3000,
      stopOnInteraction: false,
      stopOnMouseEnter: true,
      stopOnFocusIn: true,
      breakpoints: {
        "(prefers-reduced-motion: reduce)": { active: false },
      },
    })
  )
  const [carouselRef, carouselApi] = useEmblaCarousel(
    {
      align: "start",
      loop: true,
      slidesToScroll: SLIDES_TO_SCROLL,
      duration: 25,
    },
    [autoplay.current]
  )

  useEffect(() => {
    if (!carouselApi || !reviews.length) return

    const preloadImages = () => {
      const startIndex = carouselApi.selectedScrollSnap() * SLIDES_TO_SCROLL
      const visibleCount = Math.max(
        1,
        Math.ceil(
          carouselApi.rootNode().clientWidth /
            (REVIEW_IMAGE_WIDTH + REVIEW_SLIDE_GAP)
        )
      )

      setEagerImageIndexes((previous) => {
        const next = new Set(previous)
        carouselApi.slidesInView().forEach((index) => next.add(index))

        // Include partially visible cards and the next two before autoplay advances.
        for (
          let offset = 0;
          offset < visibleCount + SLIDES_TO_SCROLL;
          offset++
        ) {
          next.add((startIndex + offset) % reviews.length)
        }

        return next.size === previous.size ? previous : next
      })
    }

    preloadImages()
    carouselApi.on("select", preloadImages)
    carouselApi.on("reInit", preloadImages)

    return () => {
      carouselApi.off("select", preloadImages)
      carouselApi.off("reInit", preloadImages)
    }
  }, [carouselApi, reviews.length])

  return (
    <section
      aria-labelledby="research-reviews-title"
      className="-mx-4 bg-[#F2F3F5] sm:mx-0"
    >
      <div className="mx-auto grid max-w-[1440px] gap-8 px-3 py-[70px] min-[360px]:px-5 sm:px-[72px] lg:grid-cols-[344px_minmax(0,1fr)] xl:gap-4">
        <div className="min-w-0">
          <p className="text-[16px] leading-6 font-medium text-[#15B25C]">
            EXPERT ADVICE
          </p>
          <h2
            id="research-reviews-title"
            className="mt-4 text-[43px] leading-[54px] font-bold text-[#1D2129]"
          >
            Research
            <br />
            Before you Buy
          </h2>
          <p className="mt-4 max-w-[312px] text-[16px] leading-6 font-normal text-[#4E5969]">
            Independent reviews, side-by-side comparisons, and practical buying
            guides.
          </p>
          <Link
            href="/buying-guides"
            className="mt-[64px] inline-flex items-center gap-3 text-[14px] leading-5 font-medium text-[#0F9E4F] hover:underline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#0F9E4F] lg:mt-16"
          >
            Explore all reviews
            <ArrowRight aria-hidden="true" size={16} />
          </Link>
        </div>

        <div
          ref={carouselRef}
          role="region"
          aria-label="Expert reviews"
          aria-roledescription="carousel"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "ArrowLeft") {
              event.preventDefault()
              carouselApi?.scrollPrev()
            } else if (event.key === "ArrowRight") {
              event.preventDefault()
              carouselApi?.scrollNext()
            }
          }}
          className="min-w-0 overflow-hidden outline-none focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#0F9E4F]"
        >
          <div className="flex touch-pan-y" aria-live="off">
            {reviews.map((review, index) => (
              <ResearchReviewCard
                key={review.id}
                review={review}
                index={index}
                total={reviews.length}
                preloadImage={eagerImageIndexes.has(index)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

function ResearchReviewCard({
  review,
  index,
  total,
  preloadImage,
}: {
  review: ResearchReview
  index: number
  total: number
  preloadImage: boolean
}) {
  const [imageFailed, setImageFailed] = useState(false)
  const category = review.categories?.[0]
  const image = review.images?.[0]

  return (
    <article
      role="group"
      aria-roledescription="slide"
      aria-label={`${index + 1} of ${total}`}
      className="mr-6 min-w-0 flex-[0_0_296px] overflow-hidden rounded-[8px] bg-white"
    >
      <Link
        href={`/reviews/${category?.slug || "all"}/${review.slug}`}
        className="group block h-full focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[#0F9E4F]"
      >
        <Image
          src={image && !imageFailed ? image : fallback}
          alt={review.title}
          width={REVIEW_IMAGE_WIDTH}
          height={222}
          sizes="296px"
          loading={preloadImage ? "eager" : "lazy"}
          onError={() => setImageFailed(true)}
          className="h-[222px] w-[296px] bg-[#F2F3F5] object-cover"
        />
        <div className="p-4">
          <p className="h-4 truncate text-[12px] leading-4 font-normal text-[#86909C]">
            {category?.name}
          </p>
          <h3
            title={review.title}
            className="mt-4 line-clamp-2 h-16 text-[24px] leading-8 font-normal text-[#1D2129]"
          >
            {review.title}
          </h3>
          <p className="mt-2 line-clamp-2 h-9 text-[12px] leading-[18px] font-normal text-[#4E5969]">
            {review.abstract || review.description || ""}
          </p>
          <span className="mt-4 inline-flex items-center gap-3 text-[14px] leading-5 font-normal text-[#0F9E4F] group-hover:underline">
            Read the review
            <ArrowRight aria-hidden="true" size={16} />
          </span>
        </div>
      </Link>
    </article>
  )
}
